import { useState, useRef, useEffect } from "react";
import { useScrollIntoViewIfNotVisible } from "../../../hooks/useScrollIntoViewIfNotVisible";
import { flushSync } from "react-dom";
import { useTranslation } from "@fmc-digital/i18n";
import {
  Button,
  ErrorBanner,
  ErrorMessage,
  MFACodeInput,
  type MFACodeInputRef,
} from "@fmc-digital/ui";
import { MFADestinationText } from "../../molecules/MFADestinationText";
import { MFAResendLink } from "../../molecules/MFAResendLink";
import { MFATimer } from "../../molecules/MFATimer";
import type { MFACodeContainerProps } from "./mfa-code-container.types";
import {
  MFA_WAIT_SECONDS,
  MFA_EXPIRATION_MINUTES,
} from "./mfa-code-container.constants";


/**
 * MFACodeContainer Organism
 * Container component for MFA code verification
 */
export function MFACodeContainer({
  factorState,
  factorsList,
  onVerify,
  onCancel,
  onNavigateToFactorSelection,
  onResend,
  error,
  errorCode,
  onErrorClear,
  isLoading = false,
}: Readonly<MFACodeContainerProps>) {
  const { t } = useTranslation();
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [waitSeconds, setWaitSeconds] = useState(MFA_WAIT_SECONDS);
  const [expirationMinutes, setExpirationMinutes] = useState(MFA_EXPIRATION_MINUTES);
  const [expirationSeconds, setExpirationSeconds] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const expirationMinutesRef = useRef(MFA_EXPIRATION_MINUTES);
  const codeInputRef = useRef<MFACodeInputRef>(null);

  const [selectedFactorType] = useState<"email" | "sms">(
    factorState.factorType ?? "email",
  );
  const [selectedProfile] = useState<{
    email?: string;
    phoneNumber?: string;
  }>(factorState.profile ?? {});

  // Keep ref in sync with expirationMinutes state
  useEffect(() => {
    expirationMinutesRef.current = expirationMinutes;
  }, [expirationMinutes]);

  // Timer for wait period
  useEffect(() => {
    if (waitSeconds > 0) {
      const timer = setTimeout(() => setWaitSeconds(waitSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [waitSeconds]);

  // Timer for code expiration
  useEffect(() => {
    const handleTimerTick = () => {
      setExpirationSeconds((prevSeconds) => {
        if (prevSeconds === 0) {
          return handleMinuteDecrement();
        }
        return prevSeconds - 1;
      });
    };

    const handleMinuteDecrement = (): number => {
      // Check if minutes are already 0 before decrementing
      if (expirationMinutesRef.current === 0) {
        // Both minutes and seconds are 0, timer has expired.
        // Batch updates atomically so code is never cleared while isExpired is still false.
        flushSync(() => {
          setCode(["", "", "", "", "", ""]);
          setIsExpired(true);
        });
        return 0;
      }
      // Decrement minutes
      setExpirationMinutes((prevMinutes) => {
        expirationMinutesRef.current =
          prevMinutes > 0 ? prevMinutes - 1 : 0;
        return expirationMinutesRef.current;
      });
      // Reset seconds to 59 since we decremented minutes
      return 59;
    };

    const timer = setInterval(handleTimerTick, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleNext = () => {
    const fullCode = code.join("");
    if (fullCode.length === 6) {
      onVerify(fullCode, {
        ...factorState,
        factorType: selectedFactorType,
        profile: selectedProfile,
      });
    }
  };

  const handleSendToAlternate = () => {
    // Determine the alternate factor type
    const targetFactorType = selectedFactorType === "sms" ? "email" : "sms";

    // Check if alternate factor exists in factorsList
    if (factorsList && factorsList.length > 0) {
      const alternateFactor = factorsList.find((factor) => {
        const factorType = factor.factorType?.toLowerCase() ?? "";
        if (targetFactorType === "email") {
          return factorType.includes("email");
        } else {
          return factorType.includes("sms");
        }
      });

      // Only navigate if alternate factor exists
      if (alternateFactor) {
        onNavigateToFactorSelection(targetFactorType);
      }
    } else {
      // Fallback: navigate even if factorsList not available
      onNavigateToFactorSelection(targetFactorType);
    }
  };

  const handleResend = () => {
    // Clear error so OTP inputs don't stay in red state
    if (onErrorClear) {
      onErrorClear();
    }
    // Reset wait timer
    setWaitSeconds(MFA_WAIT_SECONDS);
    // Reset expiration timer - sync ref immediately to prevent race condition
    expirationMinutesRef.current = MFA_EXPIRATION_MINUTES;
    setExpirationMinutes(MFA_EXPIRATION_MINUTES);
    setExpirationSeconds(0);
    // Clear expiration state and code input (single source of truth - no separate effect)
    setIsExpired(false);
    setCode(["", "", "", "", "", ""]);

    // Focus first input after state update
    setTimeout(() => {
      codeInputRef.current?.focus();
    }, 0);

    // Notify parent
    onResend();
  };

  const handleCodeChange = (newCode: string[]) => {
    setCode(newCode);
    // Clear error when user starts typing a new code
    if (error && onErrorClear) {
      onErrorClear();
    }
  };

  const getAlternateOptionText = () => {
    if (selectedFactorType === "sms") {
      return t("auth.mfa.sendToEmail");
    }
    return t("auth.mfa.sendToPhone");
  };

  // Check if alternate factor exists - only show link when multiple factors available
  const hasAlternateFactor = () => {
    if (!factorsList || factorsList.length <= 1) {
      return false; // Don't show alternate option if only 1 factor or none
    }

    const targetFactorType = selectedFactorType === "sms" ? "email" : "sms";
    return factorsList.some((factor) => {
      const factorType = factor.factorType?.toLowerCase() ?? "";
      if (targetFactorType === "email") {
        return factorType.includes("email");
      } else {
        return factorType.includes("sms");
      }
    });
  };

  const isCodeComplete = code.every((digit) => digit !== "");
  // Invalid code (CUST_S1200004) shows below OTP inputs; other errors show below the card
  const isInvalidCodeError = errorCode === "CUST_S1200004";

  // Refs for banners
  const errorBannerRef = useRef<HTMLDivElement>(null);
  const expiredBannerRef = useRef<HTMLDivElement>(null);
  useScrollIntoViewIfNotVisible(errorBannerRef, !!error && !isInvalidCodeError);
  useScrollIntoViewIfNotVisible(expiredBannerRef, isExpired);

  return (
    <div className="mfa-code-wrapper w-full mx-auto px-4 md:px-0" data-testid="mfa-code-container">
      <div className="text-center mb-8 md:mb-[32px]">
        <MFADestinationText
          factorType={selectedFactorType}
          profile={selectedProfile}
        />
        <MFAResendLink
          waitSeconds={isExpired ? 0 : waitSeconds}
          onResend={handleResend}
        />
      </div>

      <div className="auth-card w-full mx-auto">
        <div className="mfa-code-content-wrapper">
          <div className="mfa-otp-stack">
            <MFACodeInput
              ref={codeInputRef}
              code={code}
              onChange={handleCodeChange}
              error={!!error}
              disabled={isExpired}
            />
            {error && isInvalidCodeError && (
              <div className="mfa-invalid-code-error-wrap">
                <ErrorMessage
                  className="mfa-invalid-code-error"
                  data-testid="mfa-invalid-code-error"
                >
                  {error}
                </ErrorMessage>
              </div>
            )}
            {!isExpired && (
              <MFATimer minutes={expirationMinutes} seconds={expirationSeconds} />
            )}
          </div>
        </div>

        {/* Button order matches personal info: Next first on mobile/tablet, Cancel first on desktop (lg) */}
        <div className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-8 mfa-code-buttons-wrapper">
          <Button
            variant="outlined"
            onClick={onCancel}
            className="w-full lg:flex-1"
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="filled"
            onClick={handleNext}
            className="w-full lg:flex-1"
            disabled={!isCodeComplete || !!error || isExpired || isLoading}
            loading={isLoading}
          >
            {t("common.next")}
          </Button>
        </div>

        {hasAlternateFactor() && (
          <div className="text-center mfa-alternate-factor-link">
            <button
              type="button"
              onClick={handleSendToAlternate}
              className="mfa-resend-link"
            >
              {getAlternateOptionText()}
            </button>
          </div>
        )}
      </div>

      {error && !isInvalidCodeError && (
        <div ref={errorBannerRef}>
          <ErrorBanner message={error} className="mt-6 md:mt-8 mb-8" />
        </div>
      )}
      {isExpired && (
        <div ref={expiredBannerRef}>
          <ErrorBanner
            message={t("errors.codeExpired")}
            className="mt-6 md:mt-8 mb-8"
          />
        </div>
      )}
    </div>
  );
}
